<template>
  <div>
    <vx-card class="mt-base mb-5" title="Learning & Resources">
      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-3/4 lg:w-2/3 w-full my-5">
          <h4 class="mb-5">The Laws that Govern Australia</h4>

          <p class="mb-5">These laws are made and operate in different ways.</p>
          <p class="mb-5">
            Let’s take a look at each. But don’t worry if you don’t fully understand it all.
          </p>
          <ul class="default-list">
            <li>Statute Law</li>
            <li>Delegated law</li>
            <li>Common law</li>
          </ul>
          <p class="mb-5">As described by the Parliamentary Education Office:</p>
          <p class="mb-5">
            Statute law is made by parliament. In the Australian Parliament, a bill is a proposal
            for a new law or a change to an existing one. A bill becomes a law after it has been
            passed in the same form by the House of Representatives and the Senate and is given
            Royal Assent by the Governor-General. It is then called an Act of Parliament.
          </p>
          <p class="mb-5">
            For a bill to be passed, it must be agreed to by a majority vote in both houses of
            Parliament. A bill may also be sent to a parliamentary committee for further
            investigation before being voted on by the Parliament.
          </p>
          <p class="mb-5">
            Delegated law - Sometimes the Parliament gives the power to make decisions about the
            details of laws it makes to the relevant minister, executive office-holder or government
            department. This is called delegated law because the power has been delegated—given—to
            that person or department.
          </p>
          <p class="mb-5">
            Delegated law allows the specific details of an existing law to be made or changed
            without having to be debated and passed by the Parliament. It can often mean
            amendments—changes—to an existing law can be made in a shorter period of time, and by
            those responsible for the particular area it covers. The Parliament usually keeps the
            right to overrule—disallow—delegated law if it does not agree with it.
          </p>
          <p class="mb-5">
            Common law is made by judges in a court, using precedent—decisions made in previous
            similar cases—to decide how they will judge a case before them. If no past cases with
            similar circumstances exist, a new decision is made, which would then become a precedent
            for a future similar case. If no statute law applies to cover a particular situation,
            common law will apply; however, statute law always overrides common law.
          </p>
          <p class="mb-5">
            Australia inherited its system of common law from Britain. The name comes from the idea
            that common law applied to everyone in society, not just those—such as religious
            ministers—who had particular rules relating to them.
          </p>
        </div>
        <div class="vx-col xl:w-1/4 lg:w-1/3 w-full my-5">
          <img
            src="@/assets/images/experiences/law/law_l_r_sidebar.jpg"
            alt=""
            class="mx-auto img-responsive"
          />
        </div>
      </div>

      <h4 class="mb-5">Areas of Law</h4>
      <p class="mb-5">
        There are many different areas of law. So, if you have a particular interest or passion for
        upholding or defending something or somebodies, you can likely specialise in an area that
        covers that desire.
      </p>
      <p class="mb-0">
        Take a look at some of the areas of law as described by the Law Institute of Victoria
      </p>
      <p class="mb-3">
        <small
          >(<strong>Note:</strong> this information is sourced from and is copyright to the Law
          Institute of Victoria (www.liv.asn.au). In places, references are made to Victoria, but
          the information is similar for other states).</small
        >
      </p>
      <p class="mb-5">
        Optional - You can click on the drop-down arrows if you would like to understand what any or
        all of them are about.
      </p>

      <vs-collapse accordion>
        <vs-collapse-item v-for="(area, index) in areas_of_law" :key="index">
          <div slot="header">{{ area.title }}</div>
          <div v-html="area.description"></div>
        </vs-collapse-item>
      </vs-collapse>

      <p class="my-5">
        In the next section “Activities” as you complete the tasks, you will have the opportunity to
        explore and consider:
      </p>

      <ul class="default-list">
        <li>What type of job in the legal sector would suit you?</li>
        <li>Whether you would you make a good layer?</li>
        <li>Whether you would you make a good legal secretary?</li>
        <li>Whether you would you make a good court or legal clerk?</li>
        <li>The Australian legal system.</li>
        <li>A virtual 3D tour of a court.</li>
        <li>Court case scenarios and who does what.</li>
      </ul>
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';

export default {
  data() {
    return {
      areas_of_law: [
        {
          title: 'Administrative Law',
          description: `Administrative law is concerned with the regulation of government decision-making at both a State and Federal level. Citizens who are adversely affected by a decision made by a government authority can have those decisions reviewed under administrative law. Statutory interpretation is a key aspect of administrative law.<br/><br/>
Individuals can apply for merits review, which is a re-consideration of an original decision. This review process can be internal (within the same agency, but by a different officer). Merits review can also be sought at a Tribunal if provided for under legislation. The main Tribunal at the Commonwealth level is the Administrative Appeals Tribunal, while at the Victorian level it is the Victorian Civil and Administrative Tribunal.<br/><br/>
Individuals can also apply for judicial review of a decision, which is carried out by a court. Judicial review looks only at the legality of the decision made, not the merits of the decision. Avenues of judicial review at a Commonwealth level are through: the Administrative Decisions (Judicial Review) Act 1977 (Cth); rights to judicial review under the Australian Constitution; and specific statutory schemes for judicial review (such as under the Migration Act 1958 (Cth).
`,
        },
        {
          title: 'Animal (Welfare) Law',
          description: `Animal law is the area of law concerned with the legislation governing the welfare and treatment of animals. Animal Law principally concerns the challenges confronting the enforcement of such laws including by way of prosecution and private challenges utilising civil remedies such as interlocutory injunctions, the rights of advocates (including by way of secondary boycotts) and protestors, and possible legal impediments to the public dissemination of the animal welfare message (such as enunciated by the High Court in the Levy ‘free speech’ case), the manner in which strategic litigation may be creatively used to improve animal welfare outcomes in reliance on laws other than animal protection statutes (with their low welfare thresholds for most animals).
`,
        },
        {
          title: 'Banking & Finance',
          description: `Banking and Finance Law refers to the collection of legal principles which impact banking and finance transactions, and the institutions involved in such transactions.<br/><br/>
Regulation and monitoring of financial institutions the subject of these laws is predominantly undertaken by the Australia Securities and Investments Commission (ASIC), the Reserve Bank of Australia and the Australia Prudential Regulation Authority.
`,
        },
        {
          title: 'Business & Corporate Law',
          description: `Business and corporate law focuses on legal issues relating to the activities of businesses, from large corporations to small and medium-sized enterprises. This includes the formation, franchising and management of small and medium-sized enterprises, as well as high-level corporations law, mergers and acquisitions and public listings.
`,
        },
        {
          title: 'Children’s Law (child protection)',
          description: `Child protection lawyers (or children’s lawyers) primarily practice in the Children’s Court and deal with matters involving children being removed, or at risk of being removed, from their families by the Department of Health and Human Services Victoria (DHHS) as well as youth justice. Child protection practitioners liaise with a number of professionals and service providers including educational providers, out of home care service providers, Victoria police, hospitals and health centres.
`,
        },
        {
          title: 'Collaborative Practice',
          description: `Collaborative practice is a method of dispute resolution whereby the parties and their lawyers contract in writing to attempt to resolve a dispute without recourse to litigation and agree in writing that the lawyers will not act for the clients if they cannot resolve their matter by collaboration and decide to litigate the dispute.<br/><br/>
Collaborative practitioners work together with other collaborative professional, including but dependent on the requirements of each individual matter financial planners, accountants and psychologists, in a collaborative process in such ways as best suit the needs of the clients.<br/><br/>
The collaboratively trained legal practitioner provides legal advice to clients and manages the process of dispute and conflict resolution whereby the clients agree upon the outcomes, when appropriate.
`,
        },
        {
          title: 'Commercial Litigation',
          description: `When commercial disputes extend beyond negotiations, commercial litigation lawyers may be engaged to advise on how to resolve the issues in dispute.<br/><br/>
Commercial litigators advise on a range of issues including general contractual principles, claims and remedies, misleading or deceptive conduct, misrepresentation and unconscionable conduct, equitable claims and remedies, contribution and proportionate liability, Statute of Limitations, and liability of directors and officers.
`,
        },
        {
          title: 'Competition and Consumer',
          description: `<strong>Competition Law</strong><br/><br/>
Competition Law relates to the legal regulation of markets to protect and encourage competition in Australia.<br/><br/>
Matters relating to anti-competitive conduct (for example, misuse of market power, horizontal and vertical restraints and resale price maintenance) are regulated under the Competition and Consumer Act 2010 (Cth) by the Australian Competition and Consumer Commission (ACCC).<br/><br/><br/><br/>

<strong>Consumer Law</strong><br/><br/>
Consumer law is the national law for fair trading and consumer protection. It is governed by the Australian Consumer Law (Schedule 2 of the Competition and Consumer Act 2010 (Cth)).<br/><br/>
Regulation of the Australian Consumer Law is managed jointly by the Australian Competition and Consumer Commission (ACCC) and the relevant state and territory consumer protection agencies.
`,
        },
        {
          title: 'Constitutional',
          description: `Constitutional lawyers primarily advise in relation to matters arising from the interpretation and application of the Australian Constitution which defines the relationship of different entities within Australia, namely, the executive, the legislature, and the judiciary. Key features of constitutional law in Australia include: federalism, inconsistencies between Commonwealth and State laws and the scope of Commonwealth and State powers. The ability to apply statutory interpretation and knowledge of constitutional principles is key to practising in constitutional law.<br/><br/>
Constitutional lawyers are often engaged by other legal practitioners to advise in relation to the constitutional validity of an Act law made by the Commonwealth or a State or Territory government, whether that law is within power or inconsistent.<br/><br/>
Similarly, the Courts in which constitutional issues may arise are varied although proceedings to determine disputes about the constitutional validity of an Act are heard by the High Court of Australia.
`,
        },
        {
          title: 'Corporate',
          description: `<p class="mb-5">
Corporate lawyers focus on the formation, management and regulation of corporate entities and the transactions undertaken by these entities.</p>
<p class="mb-5">
These matters are predominantly regulated under the <em>Corporations Act 2001 </em>(Cth) by the Australia Securities and Investments Commission (ASIC).</p>
<p class="mb-5">
Useful websites and resources, related to corporate law can be found at:</p>
<p class="mb-5">
<strong>Government</strong><br><br>
<a target="_blank" href="https://www.accc.gov.au/">https://www.accc.gov.au/</a><br>
<a target="_blank" href="http://www.victorianchamber.com.au/">http://www.victorianchamber.com.au/</a><br>
<a target="_blank" href="https://industry.gov.au/Pages/default.aspx">https://industry.gov.au/Pages/default.aspx</a><br>
<a target="_blank" href="http://dfat.gov.au/pages/default.aspx">http://dfat.gov.au/pages/default.aspx</a><br>
<a target="_blank" href="https://www.business.gov.au/">https://www.business.gov.au/</a><br>
<a target="_blank" href="https://abr.business.gov.au/">https://abr.business.gov.au/</a></p>
<p class="mb-5">
<strong>Industry and Business Organisations</strong><br><br>
<a target="_blank" href="http://becaustralia.org.au/">http://becaustralia.org.au/</a><br>
<a target="_blank" href="https://www.britishcouncil.org.au/">https://www.britishcouncil.org.au/</a><br>
<a target="_blank" href="https://www.acci.asn.au/">https://www.acci.asn.au/</a><br>
<a target="_blank" href="http://www.cweic.org/">http://www.cweic.org/</a></p>
<p class="mb-5">
<strong>Grants</strong><br><br>
<a target="_blank" href="https://www.industry.gov.au/smallbusiness/Pages/Business-Grants-Hub.aspx">https://www.industry.gov.au/smallbusiness/Pages/Business-Grants-Hub.aspx</a><br>
<a target="_blank" href="https://www.australiangovernmentgrants.org/qa/qa.php?id=ausindustry-grants">https://www.australiangovernmentgrants.org/qa/qa.php?id=ausindustry-grants</a></p>`,
        },
        {
          title: 'Costs Law',
          description: `Costs law impacts all practitioners – whether working in litigation, transactional, criminal, family law, advocacy or advisory work – as it covers the practitioner-client relationship, what lawyers can charge and, most importantly, the requirements for a valid engagement with a client.<br/><br/>
A second focus is the costs which are recoverable or payable by a party in litigation and costs which are payable under contractual terms or other arrangements. This can involve proceedings to assess the quantum of costs, preparing cost budgets in litigation, advising on cost orders and advising practitioners on the best way to manage their files to maximise cost recovery.<br/><br/>
A third focus is providing expert opinions in security for costs litigation, gross sum cost application and applications for approval of costs.`,
        },
        {
          title: 'Criminal Law',
          description: `Criminal lawyers focus on summary and indictable criminal law matters under Victorian laws, as well as criminal offences under Commonwealth legislation.<br/><br/>
The courts in which they practice include Magistrates' Court, Children’s Court, County Court, Supreme Court, Court of Appeal, and Coroner's Court.
`,
        },
        {
          title: 'Dispute Resolution (Mediation & Arbitration)',
          description: `Dispute resolution processes fall into two major types: adjudicative processes such as arbitration in which an arbitrator determines the outcome; and consensual processes, also known as alternative dispute resolution (ADR) processes, such as mediation, conciliation, or collaborative practice, in which the parties attempt to reach agreement with the assistance of a mediator/conciliator/collaborative practice professional.
`,
        },
        {
          title: 'Elder Law',
          description: `Elder law is concerned with issues affecting older persons.<br/><br/>
The legal matters that arise often intersect with many other areas of law, such as: guardianship and administration, health law, human rights, equity and trusts, succession law, property law, family law, taxation, contract law, and consumer law.<br/><br/>
Lawyers practising in elder law often liaise with other professionals including: medical practitioners, allied health, nursing and administration staff, community support services, financial planners and accountants, the Office of the Public Advocate and the Victorian Civil and Administrative Tribunal (VCAT).`,
        },
        {
          title: 'Entertainment/Arts/Media',
          description: `This area of law relates to legal issues relevant to the entertainment, arts and media industries and the individuals working within these industries.<br/><br/>

This includes a large range of legal issues including intellectual property protection and licensing, talent and sponsorship agreements, censorship and defamation.
`,
        },
        {
          title: 'Environmental and Planning',
          description: `Environmental and Planning law relates to urban, recreational and large scarce environments with the purpose to preserve and prevent destruction through development. This area of law includes consideration of the following matters and issues:<br/><br/>
<ul class="default-list">
<li>Planning</li>
<li>Subdivisions</li>
<li>Environmental controls</li>
<li>Conservation controls</li>
<li>Jurisdictions, powers and procedures</li>
</ul>
`,
        },
        {
          title: 'Equity & Trusts',
          description: `Lawyers specialising in trust law and equity primarily advise in relation to claims involving claims for equitable relief and/or issues arising from trusts (including resulting or constructive trusts as well as unit or discretionary trusts).<br/><br/>

Apart from advising clients in relation to the creation of a trust, trustees in relation to their obligations and beneficiaries of their rights pursuant to the trust deed lawyers and barristers practising in this area are often engaged by other legal practitioners for opinion regarding a discrete issue about potential equitable claims or resulting or constructive trusts).<br/><br/>

Increasingly, these issues are arising in family law cases where the interest of a party in a trust is central to determination of a property settlement or maintenance issues as well as arising in succession, commercial or property law disputes.
`,
        },
        {
          title: 'Family',
          description: `Family law is an area of law that deals with family and domestic matters including divorce, nullity, parenting arrangements, spousal maintenance, adult child maintenance and child support, child relocation, binding financial agreements, property and financial settlements, adoption and surrogacy.<br/><br/>

Family law for married and de facto parties is primarily governed by federal law (Family Law Act 1975 (Cth)) and proceedings for most matters heard in either the Family Court of Australia or Federal Circuit Court of Australia. Some aspects of family law, including adoption, surrogacy and child protection matters are governed by state legislation. For child protection matters, see “Children’s Law”<br/><br/>

It is common for family law matters to involve consideration of other areas of law including equity and trusts, property (conveyancing) law, superannuation law, tax law and work closely in collaboration with other professionals and experts including psychologists, financial planners, accountants, mediators, family consultants, real estate agents, business and property valuers, forensic experts.<br/><br/>

Family lawyers are skilled in litigation and dispute resolution practices.
`,
        },
        {
          title: 'Family Violence',
          description: `Lawyers play an important role in the way they work with clients and other professionals within the family violence system. For example, practitioners with family violence matters usually include family lawyers, criminal lawyers, child protection lawyers and duty lawyers.  The courts in which their clients may have proceedings (potentially concurrently) include the Family Courts (parenting proceedings or financial matters arising from a relationship breakdown), the Children’s Court (where there are child protection concerns), the Family Violence and Criminal Divisions of the Magistrates' Court (for intervention orders and other charges) and, in cases of serious criminal offences, the County Court or Supreme Court. Lawyers working with families affected by family violence will often liaise with Victoria police, medical centre and hospital staff, Corrections Victoria, counselling services, refuges, Department of Health and Human Services, family violence networkers, community legal centres and interpreters.<br/><br/>

To effectively support people affected by family violence, lawyers in the family violence system not only need to understand a broad range of legal issues and jurisdictions but also be aware of how the unique psychological complexities and inter-party dynamics that exist in matters involving family violence are best addressed and managed – both within the court system and the greater family violence support networks. This requires a breadth of knowledge, a range of interpersonal skills, 5 and strong inter-professional referral and collaboration. Lawyers play an integral part of the system, ensuring that all parties are adequately advised and represented, and acting as a conduit to all other support networks in the system.
`,
        },
        {
          title: 'Health & Disability Law',
          description: `Health law is a broad and interdisciplinary field that involves any law which affects the health of individuals and the public including laws regulating the health industry, the public's health, and the delivery and financing of health care services.<br/><br/>

Disability Law is an area of law that focuses more on protecting the legal rights of people with a disability.
`,
        },
        {
          title: 'Human Rights',
          description: `Human rights lawyers are concerned with the protection of basic rights and endeavours to guarantee equality for all individuals based on age, gender, orientation, race, religion, creed, nationality, colour or disability.`,
        },
        {
          title: 'Information Technology',
          description: `This area of law concerns the regulations guiding internet, software and broadcasting companies.<br/><br/>

Issues which arise in this area of law include online aspects of internet governance, Censorship and content regulation, Privacy, copyright and patents, e-Commerce issues, Cybercrime, Security and trust, Jurisdiction and conflict of laws.<br/><br/>

On an international scale, the International Association of IT Lawyers (IATL) contributes to the role of IT law in international relations and aims to promote the comparative study of IT law.
`,
        },
        {
          title: 'Insurance',
          description: `Insurance Law relates to the regulation of the insurance industry and includes regulatory frameworks to regulate insurance companies and contracts.<br/><br/>

The key legislation in this regard are the Insurance Act 1973 (Cth), the Corporations Act 2001 (Cth) and the Insurance Contracts Act 1984 (Cth). `,
        },
        {
          title: 'Intellectual Property',
          description: `Intellectual property law relates to the bundle of rights which protects creations of the mind, including inventions, literary and artistic marks, and trademarks. Intellectual Property rights include:<br/><br/>
<ul class="default-list">
<li>Copyright;</li>
<li>Trademarks;</li>
<li>Designs;</li>
<li>Patents;</li>
<li>Plant breeder’s rights;</li>
<li>Confidential information and trade secrets.</li>
</ul>

These rights are all regulated under separate pieces of legislation. Regulation of intellectual property matters is, however, centrally overseen by Intellectual Property Australia, a Federal Government agency. Intellectual property disputes are usually heard in the Federal Court and often have a trans-national element.
`,
        },
        {
          title: 'Koori Justice',
          description: `Koori justice is about social justice and human rights for Aboriginal and Torres Strait Islander peoples in Victoria. It also means recognising the distinctive rights that Aboriginal and Torres Strait Islander people hold as the original peoples of the land, including the right to a distinct status and culture, the right to self-determination and the right to land.<br/><br/>

Koori justice spans all areas of law, most commonly human rights law, family law, child protection law, criminal law and environment and planning law.
`,
        },
        {
          title: 'Leasing Law',
          description: `In Victoria, the key legislation which applies to retail leases is the Retail Leases Act 2003 (Vic). This is supplemented by the common law. There is no equivalent legislation that applies to general commercial leases in Victoria.<br/><br/>

Residential leases are governed by the Residential Tenancies Act 1998 (Vic).<br/>
Issues for practitioners to consider in leasing transactions include:<br/><br/>
<ul class="default-list">
<li>the nature of a leasehold interest in land </li>
<li>the application of contractual principles to leases </li>
<li>the application of equitable principles to leases </li>
<li>Lease disclosures </li>
<li>Preparation  and negotiation of  lease documentation including specific clauses</li>
<li>Landlord’s and tenant’s fixtures and improvements </li>
<li>Protection of leases </li>
<li>Renewal of leases </li>
<li>Termination of leases </li>
<li>Remedies </li>
<li>Legal costs in  leasing transactions </li>
<li>Stamp duty in leasing transactions </li>
<li>GST in leasing transactions</li>
</ul>
`,
        },
        {
          title: 'Liquor, Gaming & Hospitality Law',
          description: `This area of the law regulates the supply of liquor in public venues and related issues, as well as gaming machines.`,
        },
        {
          title: 'Migration and Refugee Law',
          description: `Migration and refugee law concerns the movement of people to and from Australia for reasons including permanent residency, work, study and leisure and those seeking asylum.`,
        },
        {
          title: 'Native Title',
          description: `Native title in Australia recognises the traditional rights and interests to land and waters of Aboriginal and Torres Strait Islander people. The non-discriminatory protection of native title is a recognised human right. The principles Commonwealth legislation in this area is the Native Title Act 1993 (Cth).`,
        },
        {
          title: 'Not-for-profit & Charity Law',
          description: `This area of law relates to the laws and regulations that govern the formation, management and regulation of not-for-profit entities and charities. This encompasses a broad range of regulatory schemes including:<br/><br/>
<ul class="default-list">
<li>Employment</li>
<li>Tax</li>
<li>Intellectual Property</li>
<li>Insurance</li>
<li>Legal structures and formation</li>
<li>Regulation of charities</li>
</ul>
`,
        },
        {
          title: 'Personal Injury Law',
          description: `Personal injury law applies to claims by those who have been injured, physically or psychologically, in the course of their employment; as a result of a motor vehicle accident; or as a result of the negligence or wrongdoing of another person or entity. The LIV’s Litigation Lawyers Section has two committees which deal with personal injury matters: the Transport Accident Committee, and the Public Liability & Medical Negligence Committee.`,
        },
        {
          title: 'Privacy & Surveillance',
          description: `Privacy has many facets, many of which are relevant to lawyers. Each individual will place a different value on privacy – but everyone has the benefit of some privacy protections. As lawyers, we have obligations to protect our clients’ privacy. Privacy issues cross various legal categories – every lawyer needs to at least be able to identify privacy issues affecting their clients’ matters. This is particularly imperative in today’s technologically driven world, where new technologies such as drones, biometrics and social media are requiring heightened awareness of security measures and privacy practices to help protect personal information.<br/><br/>

Technology is drastically revolutionising our understanding of, and the value we place on, privacy – but is the law keeping pace with this change? Are our current standards, rights and obligations for the access, handling and holding, use and correction of personal information appropriate for contemporary circumstances?
`,
        },
        {
          title: 'Private International Law',
          description: `Private international law is the body of conventions, model laws, national laws, legal guides, and other documents and instruments that regulate private relationships across national borders.<br/><br/>
`,
        },
        {
          title: 'Property Law',
          description: `Property Law focuses on the broad issues related to real property. Property practitioners require a good understanding of:<br/><br/>
<ul class="default-list">
<li>the conveyancing process and the issues which can arise;</li>
<li>owners corporations;</li>
<li>subdivisions;</li>
<li>taxes and duties that are levied on real property;</li>
<li>mortgages and securities;</li>
<li>property development and interaction with planning law;</li>
<li>leasing law;</li>
<li>statutory restrictions affecting real property; and</li>
<li>matters affecting title and interest, including adverse possession and compulsory acquisition.</li>
</ul>`,
        },
        {
          title: 'Public International Law',
          description: `Public international law is normally defined as the law between sovereign nation-states, especially within the context of the laws of war, peace and security, the protection of territories, international human rights, labour, finance and the natural environment.`,
        },
        {
          title: 'Restructuring and Insolvency (Bankruptcy)',
          description: `This area of law relates to legal matters relevant to companies in financial distress, most commonly when a company goes into voluntary administration, liquidation and receivership.<br/><br/>

Legal practitioners in this area work to manage the legal and practical implications for the company, its creditors or another affected party (for example banks and other financial institutions).`,
        },
        {
          title: 'Sports Law',
          description: `Sports Law relates to all legal aspects regarding sporting events, personnel and entities. It ranges from matters relating to  the regulation of the sporting industry, including the specific regulation of particular sporting codes and the individuals involved in those codes to matters of intellectual property or contractual issues as well as the formation and management of sporting entities (whether that be through companies, trusts or incorporated associations).`,
        },
        {
          title: 'Succession Law',
          description: `Succession law encompasses a number of matters related to wills and estate planning, including wills, trusts, grants of representation, legal personal representatives, powers of attorney and guardianship, intestacy issues, taxation, superannuation and insurance, family provision claims under Part IV of the Administration and Probate Act 1958 (Vic) and other estate litigation.`,
        },
        {
          title: 'Tax Law',
          description: `Tax law relates to the administration and regulation of tax at both the state and federal level. This includes a large range of matters including income tax, capital gains tax, stamp duty, land tax, and GST.`,
        },
        {
          title: 'Wills & Estates (Succession)',
          description: `Succession lawyers primarily advise clients in relation to wills and estate planning. Common areas coveredby succession lawyers in practice include:<br/><br/>
<ul class="default-list">
<li>Wills </li>
<li>Testator </li>
<li>Grants of Representation </li>
<li>Executors/Trustees </li>
<li>Administration </li>
<li>Powers of Attorney & Guardianship </li>
<li>Trusts </li>
<li>Intestacy </li>
<li>Testamentary Gifts </li>
<li>Taxation </li>
<li>Real Property </li>
<li>Superannuation & Insurance </li>
<li>Choses in Action </li>
<li>Testator's Family Maintenance / Family Provision </li>
<li>Other Estate Litigation</li>
</ul>`,
        },
        {
          title: 'Workplace Relations',
          description: `Workplace Relations Law is ever-changing in light of technological evolution, market forces and changing community standards. Workplace relations practitioners often work from either an employee or employer perspective and advise on rights, duties and obligations of each respective party to an employment agreement. Practitioners also have to navigate this multi-jurisdictional area of law and consider both state and federal laws that apply in an employment context.`,
        },
      ],
    };
  },
  computed: {
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
